<script setup lang="ts">
import type { CandidatesFeatureStep } from "@/types/marketing";
import { useIntersectionAnimation } from "@/composables/use-intersection-animation";
import VisualCreateProfile from "./visuals/VisualCreateProfile.vue";
import VisualCompleteProfile from "./visuals/VisualCompleteProfile.vue";
import VisualGetVerified from "./visuals/VisualGetVerified.vue";
import VisualApplyJobs from "./visuals/VisualApplyJobs.vue";
import VisualGetHired from "./visuals/VisualGetHired.vue";

interface Props {
  step: CandidatesFeatureStep;
  index: number;
}

const props = defineProps<Props>();
const { elementRef } = useIntersectionAnimation({ threshold: 0.1 });

const visualComponents = {
  "create-profile": VisualCreateProfile,
  "complete-profile": VisualCompleteProfile,
  "get-verified": VisualGetVerified,
  "apply-jobs": VisualApplyJobs,
  "get-hired": VisualGetHired,
};

const VisualComponent = computed(() => visualComponents[props.step.visual]);
</script>

<template>
  <div ref="elementRef" class="py-16 lg:py-24 2xl:py-32 3xl:py-40 4xl:py-48" :class="index % 2 === 0 ? 'bg-warm-white' : 'section-sand'">
    <div
      class="max-w-[1400px] 2xl:max-w-[1600px] 3xl:max-w-[1800px] 4xl:max-w-[2200px] mx-auto px-4 sm:px-6 lg:px-8 2xl:px-12 3xl:px-16 4xl:px-20"
    >
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 2xl:gap-20 3xl:gap-24 4xl:gap-32 items-center">
        <!-- Content Column -->
        <div class="space-y-6 2xl:space-y-8 3xl:space-y-10 4xl:space-y-12 fade-up" :class="step.reversed ? 'lg:order-2' : 'lg:order-1'">
          <!-- Step Number & Icon Badge -->
          <div class="inline-flex items-center gap-3 2xl:gap-4 3xl:gap-5">
            <div
              class="w-12 h-12 2xl:w-14 2xl:h-14 3xl:w-16 3xl:h-16 4xl:w-20 4xl:h-20 rounded-2xl 3xl:rounded-3xl bg-gradient-to-br from-primary/10 to-savanna/10 flex items-center justify-center"
            >
              <Icon :name="step.icon" class="w-6 h-6 2xl:w-7 2xl:h-7 3xl:w-8 3xl:h-8 4xl:w-10 4xl:h-10 text-primary" />
            </div>
            <div class="flex items-center gap-2 2xl:gap-3">
              <span class="text-2xl 2xl:text-3xl 3xl:text-4xl 4xl:text-5xl font-secondary font-bold text-gradient-warm">
                {{ step.headline }}
              </span>
            </div>
          </div>

          <!-- Title -->
          <h2
            class="font-secondary text-3xl sm:text-4xl lg:text-5xl 2xl:text-6xl 3xl:text-7xl 4xl:text-8xl font-bold text-dark-gray leading-tight !pt-0 !pb-0"
          >
            {{ step.title }}
          </h2>

          <!-- Description -->
          <p class="text-lg 2xl:text-xl 3xl:text-2xl 4xl:text-3xl text-medium-gray leading-relaxed max-w-xl 2xl:max-w-2xl 3xl:max-w-3xl">
            {{ step.description }}
          </p>

          <!-- Stat (if present) -->
          <div v-if="step.stat" class="flex items-baseline gap-4 2xl:gap-6 pt-4 2xl:pt-6">
            <div class="text-4xl 2xl:text-5xl 3xl:text-6xl 4xl:text-7xl font-secondary font-bold text-gradient-warm py-1">
              {{ step.stat.value }}
            </div>
            <div class="text-sm 2xl:text-base 3xl:text-lg 4xl:text-xl text-medium-gray font-medium">
              {{ step.stat.label }}
            </div>
          </div>
        </div>

        <!-- Visual Column -->
        <div
          v-if="VisualComponent"
          class="fade-up"
          :class="step.reversed ? 'lg:order-1' : 'lg:order-2'"
          :style="{ '--animation-delay': '200ms' }"
        >
          <component :is="VisualComponent" />
        </div>
      </div>
    </div>
  </div>
</template>
